import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { CalendarDays, Clock, Home, ChevronRight, ArrowLeft, ArrowUpRight, Phone, MapPin } from 'lucide-react';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import { BLOG_POSTS, SHOP_URL, CONTACT } from '../mock';
import Seo, { SITE } from '../components/Seo';
import Reveal from '../components/Reveal';
import ShareButtons from '../components/ShareButtons';
import RelatedPosts from '../components/RelatedPosts';

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-32 text-center">
        <h1 className="font-serif-display text-[40px] text-[#26302a]">Post not found</h1>
        <button onClick={() => navigate('/blog')} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#3D5142] text-white px-6 py-3"><ArrowLeft className="w-4 h-4" /> Back to blog</button>
      </div>
    );
  }

  const url = `${SITE}/blog/${post.slug}`;
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      headline: post.title,
      description: post.metaDescription,
      image: post.heroImg,
      datePublished: post.date,
      dateModified: post.date,
      author: { '@type': 'Person', name: post.author, jobTitle: post.authorRole },
      publisher: {
        '@type': 'Organization',
        name: 'Delivery Florist | Redwood City',
        logo: { '@type': 'ImageObject', url: post.heroImg },
      },
      keywords: post.keywords,
      about: { '@type': 'Place', name: post.place || 'Atherton, California' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE}/blog` },
        { '@type': 'ListItem', position: 3, name: post.title, item: url },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: post.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  // Render a paragraph, turning "menlofloristonline.com" into a promo link
  const renderText = (text, key) => {
    const parts = text.split('menlofloristonline.com');
    if (parts.length === 1) return <p key={key} className="text-[17px] leading-[1.8] text-[#4A4A45] mb-5">{text}</p>;
    return (
      <p key={key} className="text-[17px] leading-[1.8] text-[#4A4A45] mb-5">
        {parts.map((part, idx) => (
          <React.Fragment key={idx}>
            {part}
            {idx < parts.length - 1 && (
              <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className="text-[#C4715A] font-medium underline underline-offset-4 hover:text-[#a95a45]">menlofloristonline.com</a>
            )}
          </React.Fragment>
        ))}
      </p>
    );
  };

  return (
    <>
      <Seo title={`${post.title}`} description={post.metaDescription} path={`/blog/${post.slug}`} image={post.heroImg} jsonLd={jsonLd} type="article" publishedTime={post.date} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={post.heroImg} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#26302a]/90 via-[#26302a]/60 to-[#26302a]/35" />
        </div>
        <div className="relative max-w-3xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <nav className="flex items-center gap-2 text-[13px] text-[#DCE3D6] mb-6">
            <Link to="/" className="inline-flex items-center gap-1 hover:text-white transition-colors"><Home className="w-3.5 h-3.5" /> Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
          </nav>
          <h1 className="font-serif-display text-[34px] sm:text-[46px] text-white leading-[1.1]">{post.title}</h1>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-3 text-[14px] text-[#E4E9DE]">
            <span className="inline-flex items-center gap-2 whitespace-nowrap">
              <span className="w-9 h-9 rounded-full bg-[#C4715A] flex items-center justify-center text-white font-medium">{post.author.charAt(0)}</span>
              By {post.author}
            </span>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><CalendarDays className="w-4 h-4" /> {post.dateDisplay}</span>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><Clock className="w-4 h-4" /> {post.readTime}</span>
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Reveal>
            {post.intro.map((t, i) => renderText(t, `intro-${i}`))}
          </Reveal>

          {post.relatedLocation && (
            <Reveal>
              <Link to={`/flower-delivery/${post.relatedLocation.slug}`} className="flex items-center gap-3 rounded-2xl bg-[#EDE7DC] border border-[#E1D7C7] px-5 py-4 mb-2 hover:border-[#3D5142] transition-colors group">
                <MapPin className="w-5 h-5 text-[#C4715A] shrink-0" />
                <span className="text-[15px] text-[#4A4A45]">See our dedicated page: <span className="font-medium text-[#3D5142] group-hover:text-[#C4715A] transition-colors">{post.relatedLocation.label}</span></span>
                <ArrowUpRight className="w-4.5 h-4.5 text-[#3D5142] ml-auto shrink-0" />
              </Link>
            </Reveal>
          )}

          {post.sections.map((s, si) => (
            <Reveal key={si}>
              <div className="mt-10">
                <h2 className="font-serif-display text-[28px] sm:text-[32px] text-[#26302a] leading-tight mb-5">{s.h2}</h2>
                {s.body && s.body.map((t, i) => renderText(t, `s${si}-b${i}`))}
                {s.bullets && (
                  <ul className="space-y-3 mb-5">
                    {s.bullets.map((b, i) => (
                      <li key={i} className="flex gap-3 text-[17px] leading-[1.7] text-[#4A4A45]">
                        <span className="shrink-0 mt-2.5 w-1.5 h-1.5 rounded-full bg-[#C4715A]" /> {b}
                      </li>
                    ))}
                  </ul>
                )}
                {s.after && s.after.map((t, i) => renderText(t, `s${si}-a${i}`))}
              </div>
            </Reveal>
          ))}

          {/* Promo CTA */}
          <Reveal>
            <div className="mt-12 rounded-[24px] bg-[#F1ECE3] p-8 text-center">
              <h3 className="font-serif-display text-[26px] text-[#26302a]">{post.ctaTitle || (post.place ? `Send flowers in ${post.place.split(',')[0]} today` : 'Send fresh flowers today')}</h3>
              <p className="mt-3 text-[16px] text-[#5A554C] max-w-lg mx-auto">Order before 2 PM for same-day delivery. Browse fresh arrangements and let us handle the rest.</p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#C4715A] hover:bg-[#a95a45] text-white text-[15px] font-medium px-7 py-3.5 transition-colors">Order at menlofloristonline.com <ArrowUpRight className="w-4.5 h-4.5" /></a>
                <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 rounded-full border border-[#3D5142] text-[#3D5142] hover:bg-[#3D5142] hover:text-white text-[15px] font-medium px-7 py-3.5 transition-colors"><Phone className="w-4 h-4" /> {CONTACT.phone}</a>
              </div>
            </div>
          </Reveal>

          {post.localLinks && (
            <Reveal>
              <div className="mt-10">
                <h2 className="font-serif-display text-[24px] text-[#26302a] mb-4">Local same-day delivery pages</h2>
                <div className="flex flex-wrap gap-3">
                  {post.localLinks.map((l) => (
                    <Link key={l.slug} to={`/flower-delivery/${l.slug}`} data-testid={`local-link-${l.slug}`} className="inline-flex items-center gap-2 rounded-full border border-[#DCD3C3] bg-white px-4 py-2 text-[14px] text-[#3D5142] hover:border-[#3D5142] hover:text-[#C4715A] transition-colors">
                      <MapPin className="w-3.5 h-3.5 text-[#C4715A]" /> {l.label}
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          )}

          {/* FAQ */}
          <Reveal>
            <div className="mt-16">
              <h2 className="font-serif-display text-[28px] sm:text-[32px] text-[#26302a] leading-tight mb-6">Frequently asked questions</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {post.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-2xl border border-[#EFE8DD] px-5 overflow-hidden">
                    <AccordionTrigger className="text-left font-serif-display text-[18px] text-[#2A2A28] hover:no-underline py-5">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-[15.5px] leading-relaxed text-[#5A554C] pb-5">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Reveal>

          <div className="mt-14 pt-8 border-t border-[#EAE2D6]">
            <ShareButtons url={url} title={post.title} />
          </div>

          <RelatedPosts currentSlug={post.slug} />

          <div className="mt-8 pt-8 border-t border-[#EAE2D6]">
            <Link to="/blog" className="inline-flex items-center gap-2 text-[15px] font-medium text-[#3D5142] hover:text-[#C4715A] transition-colors"><ArrowLeft className="w-4 h-4" /> Back to all articles</Link>
          </div>
        </div>
      </article>
    </>
  );
}
